/*
 * Copyright (C) 2016 Spanning Cloud Apps LLC.  All rights reserved.
 */
import './avgBackupTimeModal.tpl.html';

/**
 * Module for the avg backup time modal.
 */
angular
  .module('shared.avgBackupTimeModal', [])
  /**
   * Avg Backup time controller.  Controller for the avgBackupTimeModal.tpl.html template.
   */
  .controller('AvgBackupTimeModalCtrl', function ($scope, $uibModalInstance, batchUtils, BATCH_LIMITS) {
    /**
     * Helper function to format the data the way we need it
     *
     * @param {Array} data - The data set to be parsed.
     *
     * @return {Array} The formatted data object.
     */
    function formatData(data) {
      var SECONDS_PER_HOUR = 3600,
        totalSecs = 0,
        totalHrs = 0,
        avgSecs,
        avgHrs;

      _.each(data, function (item) {
        // date is when the backup started. We're going to display it in UTC.
        item.date = moment(item.date).utc().toDate();

        // duration is in seconds. We're going to display it in hours.
        item.durationHrs = item.duration / SECONDS_PER_HOUR;

        totalSecs += item.duration;
        totalHrs += item.durationHrs;

        return item;
      });

      // dividing by array length instead of 8 all the time in case there are not 8 batches yet.
      avgSecs = totalSecs / data.length;
      avgHrs = totalHrs / data.length;

      // add in average
      _.each(data, function (item) {
        item.avgSecs = avgSecs;
        item.avgHrs = avgHrs;
      });

      // sort in increasing date order
      data = _.sortBy(data, 'date');

      return data;
    }

    /**
     * Function used to load the batches from the batchUtils service. It's published in the scope so we can call it
     * when testing.
     */
    $scope.loadBatches = function () {
      return batchUtils
        .getBatches(BATCH_LIMITS.NUM_BATCHES_IN_BACKUP_TIME_CHART, /*refresh=*/ true)
        .then(function (batchesInfo) {
          $scope.batchData = formatData(batchesInfo.rows);
        });
    };

    $scope.showLoadingMask = true;
    $scope.loadBatches().finally(function () {
      $scope.showLoadingMask = false;
    });

    /**
     * Function to cancel out of the model.
     */
    $scope.cancel = function () {
      $uibModalInstance.dismiss('cancel');
    };
  })
  .directive('avgBackupTimeChart', function ($compile, $filter) {
    return {
      restrict: 'E',
      scope: {
        backupTimeData: '='
      },
      link: function (scope, element) {
        var Y_TICK_COUNT = 5,
          DOT_RADIUS = 4,
          // Set the size of the fixed elements
          VIEW_WD = 640,
          VIEW_HT = 200,
          VIEW_MARGIN_RIGHT = 40,
          // The text at the top showing the number of backups
          COUNT_HT = 20,
          // The legend at the bottom
          LEGEND_HT = 20,
          LEGEND_MARGIN_TOP = 10,
          // The axes
          YAXIS_TITLE_WD = 20,
          XAXIS_TITLE_HT = 20,
          XAXIS_TITLE_FONT_HT = 12,
          YAXIS_WD = 40,
          XAXIS_HT = 40,
          // The chart takes up the remaining space
          CHART_WD = VIEW_WD - YAXIS_TITLE_WD - YAXIS_WD - VIEW_MARGIN_RIGHT,
          CHART_HT = VIEW_HT - COUNT_HT - XAXIS_HT - XAXIS_TITLE_HT - LEGEND_HT,
          i18n = $filter('i18n'),
          pluralize = $filter('pluralize'),
          hourFormatter = d3.format('.2f'),
          svg,
          countBox,
          chartBox,
          xAxisBox,
          yAxisBox,
          xAxisTitleBox,
          yAxisTitleBox,
          legendBox,
          xScale,
          yScale,
          xAxis,
          yAxis,
          newSvg;

        /**
         * Builds a text description of a backup time duration, like "3 days, 4 hours, 1 minute".
         *
         * @param {Date} start - The beginning of the backup
         * @param {Number} durationInSecs - The length of the backup in seconds
         *
         * @return {String} The text description of the duration
         */
        function mixedDurationText(start, durationInSecs) {
          var text = '',
            startMoment,
            endMoment,
            daysDiff,
            hoursDiff,
            minsDiff;

          // We'll use the conveniences of moment.
          startMoment = moment(start);
          endMoment = startMoment.clone().add(durationInSecs, 'seconds');

          // See how many days long the interval is
          daysDiff = endMoment.diff(startMoment, 'days');
          if (daysDiff > 0) {
            text += pluralize(daysDiff, 'ONE_DAY', 'N_DAYS');
            // Remove the days so we are left with just hours and minutes
            endMoment.subtract(daysDiff, 'days');
          }

          // See how many hours long the remaining interval is
          hoursDiff = endMoment.diff(startMoment, 'hours');
          if (hoursDiff > 0) {
            if (text) {
              text += ', ';
            }
            text += pluralize(hoursDiff, 'ONE_HOUR', 'N_HOURS');
            // Remove the hours so we are left with just minutes
            endMoment.subtract(hoursDiff, 'hours');
          }

          // See how many minutes long the remaining interval is
          minsDiff = endMoment.diff(startMoment, 'minutes');
          if (minsDiff > 0) {
            if (text) {
              text += ', ';
            }
            text += pluralize(minsDiff, 'ONE_MINUTE', 'N_MINUTES');
          }

          if (!text) {
            text = i18n('LESS_THAN_1_MINUTE');
          }

          return text;
        }

        /**
         * Sets up the layout and creates the SVG elements for drawing the chart.
         */
        function init() {
          var countX,
            countY,
            chartX,
            chartY,
            yAxisTitleX,
            yAxisTitleY,
            yAxisX,
            yAxisY,
            xAxisX,
            xAxisY,
            xAxisTitleX,
            xAxisTitleY,
            legendX,
            legendY;

          /**
           * Creates an SVG container
           *
           * @param {String} name - The name of the container
           * @param {Number} x - The x coordinate of the container
           * @param {Number} y - The y coordinate of the container
           *
           * @return {Object} The new container
           */
          function createContainer(name, x, y) {
            return svg
              .append('g')
              .attr('transform', 'translate(' + x + ',' + y + ')')
              .attr('class', name);
          }

          // Remove any existing element. We'll recreate it.
          element.find('svg').remove();
          svg = d3
            .select(element[0])
            .append('svg')
            .attr('viewBox', '0 0 ' + VIEW_WD + ' ' + VIEW_HT)
            .attr('preserveAspectRatio', 'none');

          // Create containers for each part of the view so we don't have to worry about translations when we're
          // drawing inside each container.

          // Position the chart.
          chartX = YAXIS_TITLE_WD + YAXIS_WD;
          chartY = COUNT_HT;
          chartBox = createContainer('chart', chartX, chartY);

          // Position the label showing the number of backups
          countX = chartX;
          countY = 0;
          countBox = createContainer('count', countX, countY);

          // Position the Y axis title
          yAxisTitleX = 0;
          yAxisTitleY = chartY;
          yAxisTitleBox = createContainer('axis-title y-axis', yAxisTitleX, yAxisTitleY);

          // Position the Y axis
          yAxisX = yAxisTitleX + YAXIS_TITLE_WD;
          yAxisY = chartY;
          yAxisBox = createContainer('axis y-axis', yAxisX, yAxisY);

          // Position the X axis
          xAxisX = chartX;
          xAxisY = chartY + CHART_HT;
          xAxisBox = createContainer('axis x-axis', xAxisX, xAxisY);

          // Position the X axis title
          xAxisTitleX = chartX;
          xAxisTitleY = xAxisY + XAXIS_HT;
          xAxisTitleBox = createContainer('axis-title x-axis', xAxisTitleX, xAxisTitleY);

          // Position the legend
          legendX = chartX;
          legendY = xAxisTitleY + XAXIS_TITLE_HT + LEGEND_MARGIN_TOP;
          legendBox = createContainer('legend', legendX, legendY);
        }

        /**
         * Creates the X axis
         *
         * @param {Object} data - The batch info
         */
        function createXAxis(data) {
          // scaleBand is a variant of ordinal to display equal sized bars.
          xScale = d3
            .scaleBand()
            .domain(
              data.map(function (batch) {
                return batch.date;
              })
            )
            .range([0, CHART_WD])
            .padding(0.1);

          xAxis = d3
            .axisBottom(xScale)
            // Return blank here because we want to draw our own in drawXAxis().
            .tickFormat(function () {
              return '';
            });
        }

        /**
         * Creates the Y axis
         *
         * @param {Object} data - The batch info
         */
        function createYAxis(data) {
          yScale = d3
            .scaleLinear()
            // We always want to show 0 at the bottom of the y axis
            .domain([
              0,
              d3.max(data, function (batch) {
                return batch.durationHrs;
              })
            ])
            .range([CHART_HT, 0])
            .nice();

          yAxis = d3.axisLeft(yScale).ticks(Y_TICK_COUNT).tickFormat(hourFormatter);
        }

        /**
         * Draws the chart background
         *
         * @param {Object} data - The batch info
         */
        function drawBackground(data) {
          chartBox.append('rect').attr('class', 'chart-background').attr('width', CHART_WD).attr('height', CHART_HT);

          // If there is no data show a message here
          if (data.length === 0) {
            chartBox
              .append('text')
              .attr('class', 'no-data')
              .attr('text-anchor', 'middle')
              .attr('x', CHART_WD / 2)
              .attr('y', CHART_HT / 2)
              .text(i18n('NO_BACKUPS_AVAILABLE'));
          }
        }

        /**
         * Draws the x axis, grid lines, and tick marks
         */
        function drawXAxis() {
          var texts;

          /**
           * Creates the vertical grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeXAxisGrid(g) {
            var xAxisGrid = d3.axisBottom(xScale).tickSize(CHART_HT).tickFormat('');

            g.call(xAxisGrid);
          }

          // Draw the tick marks. 'texts' will be the set of text elements corresponding to the ticks.
          texts = xAxisBox
            .append('g')
            .attr('class', 'axis')
            .call(xAxis)
            .selectAll('g') // Select all ticks
            .selectAll('text') // Select the text within each tick
            .attr('text-anchor', 'middle');

          // Add a tspan to each tick text showing the month and day
          texts
            .append('tspan')
            .attr('x', 0)
            .text(function (date) {
              // TODO Use locale-specific format? Moment doesn't have one that doesn't include the year.
              return date ? moment(date).format('MMM D') : null;
            });

          // Add a tspan to each tick text showing the time
          texts
            .append('tspan')
            .attr('x', 0)
            .attr('dy', XAXIS_TITLE_FONT_HT)
            .text(function (date) {
              // TODO Use locale-specific time (LT) if we can find a locale-specific date, above
              return date ? moment(date).format('h:mm a') : null;
            });

          // Draw the vertical grid lines
          chartBox.append('g').attr('class', 'grid').call(makeXAxisGrid);

          // Draw the title
          xAxisTitleBox
            .append('text')
            .attr('class', 'capitalize')
            .attr('text-anchor', 'middle')
            .attr('x', CHART_WD / 2)
            .attr('y', XAXIS_TITLE_HT / 2)
            .text(i18n('BACKUP_START_TIME'));
        }

        /**
         * Draws the y axis, grid lines, and tick marks
         */
        function drawYAxis() {
          /**
           * Creates the horizontal grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeYAxisGrid(g) {
            var yAxisGrid = d3.axisLeft(yScale).ticks(Y_TICK_COUNT).tickSize(-CHART_WD).tickFormat('');

            g.call(yAxisGrid);
          }

          // Draw the y axis and tick marks.
          yAxisBox
            .append('g')
            .attr('class', 'axis')
            // A left axis is drawn to the left of its position so we move it all the way to the right
            .attr('transform', 'translate(' + YAXIS_WD + ',0)')
            .call(yAxis);

          // Draw the horizontal grid lines
          chartBox.append('g').attr('class', 'grid').call(makeYAxisGrid);

          // Draw the rotated title
          yAxisTitleBox
            .append('text')
            .attr('text-anchor', 'middle')
            .attr('transform', 'translate(' + YAXIS_TITLE_WD / 2 + ',' + CHART_HT / 2 + ') rotate(-90)')
            .text(i18n('BACKUP_DURATION_HOURS'));
        }

        /**
         * Draws the horizontal line across the chart for the average backup time.
         *
         * @param {Object} data - The batch info
         */
        function drawAverage(data) {
          /**
           * Creates the horizontal grid we use to show the average backup time value.
           *
           * @param {Object} g - the SVG container
           */
          function makeAvgGrid(g) {
            var avgGrid = d3.axisLeft(yScale).tickValues([data[0].avgHrs]).tickSize(-CHART_WD).tickFormat('');

            g.call(avgGrid);
          }

          // Draw the average backup time as a single grid line. We use a grid line instead of a chart line so that
          // we can make it go all the way across.
          chartBox.append('g').attr('class', 'average').call(makeAvgGrid);
        }

        /**
         * Draws a line graph showing the duration for each backup
         *
         * @param {Object} data - The batch info
         */
        function drawTimes(data) {
          var X_OFFSET = xScale.bandwidth() / 2,
            path,
            times;

          path = d3
            .line()
            .x(function (batch) {
              return xScale(batch.date) + X_OFFSET;
            })
            .y(function (batch) {
              return yScale(batch.durationHrs);
            });

          // Add the line graph
          times = chartBox.append('g').attr('class', 'times');

          times.append('path').datum(data).attr('d', path).attr('class', 'times');

          // Add the dots
          times
            .selectAll('circle')
            .data(data)
            .enter()
            .append('circle')
            .attr('class', function (batch) {
              return batch.type;
            })
            .attr('cx', function (batch) {
              return xScale(batch.date) + X_OFFSET;
            })
            .attr('cy', function (batch) {
              return yScale(batch.durationHrs);
            })
            .attr('r', DOT_RADIUS)
            .attr('uib-tooltip-html', function (batch) {
              var tooltip = '';

              // Show the backup type (manual, scheduled, custom).
              tooltip += '<div class="capitalize">' + i18n('BACKUP_TYPE_' + batch.type) + '</div>';

              // Show the start time. 'llll' is the full locale-specific date and time, like 'Thu, Sep 4 1986 8:30 PM'
              tooltip += '<div>' + moment(batch.date).format('llll') + '</div>';

              // Show the duration in a readable way, like '4 hours, 3 minutes'.
              tooltip += '<div>' + mixedDurationText(batch.date, batch.duration) + '</div>';

              // UI Bootstrap 0.14.3 requires HTML to be a string inside a string.
              return "'" + tooltip + "'";
            })
            .attr('tooltip-append-to-body', true);
        }

        /**
         * Draws the number of backups
         *
         * @param {Object} data - The batch info
         */
        function drawCount(data) {
          var countText;

          // Show the number of backups
          if (data.length < 8) {
            countText = pluralize(data.length, 'ONE_BACKUP', 'N_BACKUPS');
          } else {
            countText = i18n('LAST_8_BACKUPS');
          }

          countBox
            .append('text')
            .attr('class', 'primary-font-light capitalize sbsf-last-backups')
            .attr('x', 0)
            .attr('y', COUNT_HT / 2)
            .text(countText);
        }

        /**
         * Draws the legend for the chart.
         *
         * @param {Object} data - The batch info
         */
        function drawLegend(data) {
          var LINE_LENGTH = 30,
            LABEL_LEFT_MARGIN = 5,
            AUTO_LABEL_WIDTH = 80,
            MANUAL_LABEL_WIDTH = 55,
            DOT_Y = 0,
            LABEL_Y = 4,
            autoDotX,
            autoLabelX,
            manualDotX,
            manualLabelX,
            avgLineX,
            avgLabelX;

          // Show the automated backups
          autoDotX = DOT_RADIUS / 2;
          legendBox
            .append('circle')
            .attr('class', 'SCHEDULED_BACKUP')
            .attr('cx', autoDotX)
            .attr('cy', DOT_Y)
            .attr('r', DOT_RADIUS);

          autoLabelX = autoDotX + DOT_RADIUS + LABEL_LEFT_MARGIN;
          legendBox
            .append('text')
            .attr('class', 'primary-font-light capitalize')
            .attr('x', autoLabelX)
            .attr('y', LABEL_Y)
            .text(i18n('BACKUP_TYPE_SCHEDULED_BACKUP'));

          // Show the manual backups
          manualDotX = autoLabelX + AUTO_LABEL_WIDTH;
          legendBox
            .append('circle')
            .attr('class', 'MANUAL_BACKUP')
            .attr('cx', manualDotX)
            .attr('cy', DOT_Y)
            .attr('r', DOT_RADIUS);

          manualLabelX = manualDotX + DOT_RADIUS + LABEL_LEFT_MARGIN;
          legendBox
            .append('text')
            .attr('class', 'primary-font-light capitalize')
            .attr('x', manualLabelX)
            .attr('y', LABEL_Y)
            .text(i18n('BACKUP_TYPE_MANUAL_BACKUP'));

          // Show the average time
          avgLineX = manualLabelX + MANUAL_LABEL_WIDTH;
          legendBox
            .append('line')
            .attr('class', 'average')
            .attr('x1', avgLineX)
            .attr('y1', DOT_Y)
            .attr('x2', avgLineX + LINE_LENGTH)
            .attr('y2', DOT_Y);

          avgLabelX = avgLineX + LINE_LENGTH;
          legendBox
            .append('text')
            .attr('class', 'primary-font-light')
            .attr('x', avgLabelX)
            .attr('y', LABEL_Y)
            .text(i18n('BACKUP_AVERAGE', [mixedDurationText(data[0].date, data[0].avgSecs)]));
        }

        scope.$watch('backupTimeData', function (newVal) {
          if (!newVal) {
            return;
          }

          init();
          createXAxis(newVal);
          createYAxis(newVal);
          drawBackground(newVal);

          // Only draw these elements if there is at least one data point.
          if (newVal.length) {
            drawYAxis();
            drawXAxis();
            drawAverage(newVal);
            drawTimes(newVal);
            drawLegend(newVal);
            drawCount(newVal);
          }

          // Since we are using a directive for the tooltips
          // we will need to recompile those elements.
          newSvg = element.find('svg').clone();
          newSvg = $compile(newSvg)(scope);
          element.find('svg').replaceWith(newSvg);
        });
      }
    };
  });
